<!--
  This is own datepicker to implement required attribute.
-->
<template>
  <div ref="datePicker">
    <el-date-picker
      v-model="date"
      :type="type"
      size="large"
      :class="{ 'not-empty': isNotEmpty }"
      :placeholder="placeholder"
      :start-placeholder="startPlaceholder"
      :end-placeholder="endPlaceholder"
      :range-separator="rangeSeparator"
      :format="format"
      :editable="editable"
      :disabled-date="disabledDate"
      :disabled="disabled"
      :default-time="isRangeType ? defaultTime : undefined"
      @change="change"
    />
    <slot name="comment" />
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, computed, WritableComputedRef, watch, nextTick, onMounted } from 'vue';

const defaultTime = [new Date(2000, 1, 1, 0, 0, 0), new Date(2000, 1, 1, 23, 59, 59)];

const props = defineProps({
  modelValue: {
    type: [Date, String, Array],
    default: null,
  },

  type: {
    type: String,
    validator: (prop: string) =>
      prop === 'date' || prop === 'dates' || prop === 'daterange' || prop === 'datetime' || prop === 'datetimerange',
    default: 'date',
  },

  required: {
    type: Boolean,
    default: false,
  },

  editable: {
    type: Boolean,
    default: false,
  },

  placeholder: {
    type: String,
    default: 'Pick a date',
  },

  startPlaceholder: {
    type: String,
    default: 'Start date',
  },

  endPlaceholder: {
    type: String,
    default: 'End date',
  },

  rangeSeparator: {
    type: String,
    default: 'To',
  },

  format: {
    type: String,
    default: 'YYYY-MM-DD',
  },

  disabledDate: {
    type: Function,
    default: () => false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const datePicker: Ref<any> = ref(null);

const date: WritableComputedRef<any> = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const isRangeType = computed(() => props.type === 'daterange' || props.type === 'datetimerange');

const isNotEmpty = computed(() => date.value && date.value !== '');

const change = () => {
  emit('change');
};

const addRequiredAttribute = (inputElement: HTMLInputElement) => {
  inputElement.readOnly = false;
  inputElement.required = true;
};

const removeRequiredAttribute = (inputElement: HTMLInputElement) => {
  inputElement.readOnly = true;
  inputElement.required = false;
};

watch(
  () => props.required,
  (value: boolean) => {
    const inputElement = isRangeType.value
      ? (datePicker.value.querySelector('input.el-range-input') as HTMLInputElement)
      : (datePicker.value.querySelector('input.el-input__inner') as HTMLInputElement);

    if (!inputElement) {
      return;
    }

    if (value) {
      addRequiredAttribute(inputElement);
    } else {
      removeRequiredAttribute(inputElement);
    }
  },
);

onMounted(async () => {
  await nextTick();

  if (!props.required) {
    return;
  }

  if (!datePicker.value) {
    return;
  }

  const inputElement = isRangeType.value
    ? (datePicker.value.querySelector('input.el-range-input') as HTMLInputElement)
    : (datePicker.value.querySelector('input.el-input__inner') as HTMLInputElement);

  addRequiredAttribute(inputElement);
});
</script>
